import { useCallback, useEffect, useState } from "react";
import { Loader2, LogOut, RefreshCw, Inbox } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "gmi_admin_token";

const formatError = (detail) => {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return String(detail);
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [enquiries, setEnquiries] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadEnquiries = useCallback(
    async (tok) => {
      setBusy(true);
      try {
        const res = await fetch(`${API}/admin/enquiries`, {
          headers: { Authorization: `Bearer ${tok}` },
          credentials: "include",
        });
        if (res.status === 401) {
          localStorage.removeItem(TOKEN_KEY);
          setToken("");
          return;
        }
        const data = await res.json();
        setEnquiries(Array.isArray(data) ? data : []);
      } catch {
        toast.error("Could not load enquiries.");
      } finally {
        setBusy(false);
      }
    },
    []
  );

  useEffect(() => {
    if (token) loadEnquiries(token);
  }, [token, loadEnquiries]);

  const onLogin = async (e) => {
    e.preventDefault();
    setAuthError("");
    setBusy(true);
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatError(data.detail));
      localStorage.setItem(TOKEN_KEY, data.access_token);
      setToken(data.access_token);
      toast.success(`Welcome back, ${data.user?.name || "Admin"}.`);
    } catch (err) {
      setAuthError(err.message || "Login failed.");
    } finally {
      setBusy(false);
    }
  };

  const onLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setEnquiries(null);
  };

  if (!token) {
    return (
      <main data-testid="admin-login-page" className="min-h-screen bg-espresso-900 flex items-center justify-center px-6 grain relative">
        <form
          onSubmit={onLogin}
          data-testid="admin-login-form"
          className="w-full max-w-md border border-espresso-600 bg-espresso-800 p-10 space-y-6"
        >
          <div className="text-center">
            <img
              src="/logo-full.png"
              alt="Golden Mile Interiors"
              className="h-32 w-auto mx-auto"
              data-testid="admin-login-logo"
            />
            <p className="text-[10px] tracking-[0.25em] uppercase text-cream-muted mt-4">Workshop Login</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="admin-email" className="text-xs tracking-[0.15em] uppercase text-cream-muted">Email</Label>
            <Input
              id="admin-email"
              data-testid="admin-login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-espresso-900 border-espresso-600 text-cream rounded-none focus-visible:ring-brass h-12"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="admin-password" className="text-xs tracking-[0.15em] uppercase text-cream-muted">Password</Label>
            <Input
              id="admin-password"
              data-testid="admin-login-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-espresso-900 border-espresso-600 text-cream rounded-none focus-visible:ring-brass h-12"
            />
          </div>
          {authError && (
            <p data-testid="admin-login-error" className="text-sm text-red-400 border border-red-900/50 bg-red-950/30 px-4 py-3">
              {authError}
            </p>
          )}
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-brass text-espresso-900 font-semibold py-3.5 hover:bg-brass-hover transition-colors disabled:opacity-60"
          >
            {busy && <Loader2 size={16} className="animate-spin" />}
            {busy ? "Signing in…" : "Sign In"}
          </button>
          <a href="/" data-testid="admin-back-home" className="block text-center text-xs text-cream-muted hover:text-brass transition-colors">
            ← Back to website
          </a>
        </form>
      </main>
    );
  }

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-espresso-900">
      <header className="border-b border-espresso-600 bg-espresso-800/60 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-mark.png" alt="Golden Mile Interiors" className="h-8 w-auto" />
            <p className="font-serif text-cream">Enquiries <span className="italic text-brass">Inbox</span></p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => loadEnquiries(token)}
              data-testid="admin-refresh-button"
              className="inline-flex items-center gap-2 text-xs text-cream-muted border border-espresso-600 px-4 py-2 hover:border-brass hover:text-brass transition-colors"
            >
              <RefreshCw size={14} /> Refresh
            </button>
            <button
              onClick={onLogout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 text-xs text-espresso-900 bg-brass font-semibold px-4 py-2 hover:bg-brass-hover transition-colors"
            >
              <LogOut size={14} /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        {!enquiries || busy ? (
          <p className="text-cream-muted text-sm inline-flex items-center gap-2" data-testid="admin-loading">
            <Loader2 size={16} className="animate-spin" /> Loading enquiries…
          </p>
        ) : enquiries.length === 0 ? (
          <div className="border border-espresso-600 bg-espresso-800 p-16 text-center" data-testid="admin-empty-state">
            <Inbox size={32} className="text-espresso-600 mx-auto" />
            <p className="font-serif text-xl text-cream mt-6">No enquiries yet</p>
            <p className="text-sm text-cream-muted mt-2">Quote requests from the website will appear here.</p>
          </div>
        ) : (
          <div className="border border-espresso-600 overflow-x-auto" data-testid="admin-enquiries-table-wrapper">
            <table className="w-full text-sm border-collapse" data-testid="admin-enquiries-table">
              <thead>
                <tr className="bg-espresso-800 text-left">
                  {["Received", "Name", "Contact", "Project Type", "Message", "Status"].map((h) => (
                    <th key={h} className="text-[10px] tracking-[0.2em] uppercase text-brass font-medium px-5 py-4 border-b border-espresso-600 whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {enquiries.map((enq, i) => (
                  <tr key={enq.id} data-testid={`admin-enquiry-row-${i}`} className="border-b border-espresso-600 hover:bg-espresso-800/60 transition-colors align-top">
                    <td className="px-5 py-4 text-cream-muted whitespace-nowrap" data-testid={`admin-enquiry-date-${i}`}>
                      {new Date(enq.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                    </td>
                    <td className="px-5 py-4 text-cream font-medium whitespace-nowrap">{enq.name}</td>
                    <td className="px-5 py-4 text-cream-muted">
                      <div>{enq.email}</div>
                      {enq.phone && <div className="text-xs mt-1">{enq.phone}</div>}
                    </td>
                    <td className="px-5 py-4 text-cream whitespace-nowrap">{enq.project_type}</td>
                    <td className="px-5 py-4 text-cream-muted max-w-sm">
                      <p className="line-clamp-3">{enq.message}</p>
                    </td>
                    <td className="px-5 py-4">
                      <span className="text-[10px] tracking-[0.2em] uppercase border border-brass/40 text-brass px-3 py-1" data-testid={`admin-enquiry-status-${i}`}>
                        {enq.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </main>
  );
}
